import React from "react"
import { Finder, LocationMap } from "@components"
import { graphql, useStaticQuery } from "gatsby"
import style from "./location-map-with-finder.mod.scss"

const SHOWROOM_BASE = "find-a-showroom"

/**
 * Parse a Dato showroom list path into region vs child-of-region.
 * Expected: /find-a-showroom/{region}/ or /find-a-showroom/{region}/{child}/...
 */
function parseShowroomListPath(path) {
  if (!path || typeof path !== "string") return null
  const segments = path
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .filter(Boolean)
  const baseIdx = segments.indexOf(SHOWROOM_BASE)
  if (baseIdx === -1) return null
  const after = segments.slice(baseIdx + 1)
  if (after.length === 0) return null
  if (after.length === 1) {
    return { type: "region", regionSlug: after[0] }
  }
  return { type: "child", regionSlug: after[0] }
}

function slugToHeading(slug) {
  return slug
    .split("-")
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}

/**
 * Group showroom list nodes by regional landing (first path segment after /find-a-showroom/).
 * Order follows `showroomNodes` (Dato position sort).
 */
function buildGroupedLocationLists(showroomNodes) {
  const regionBySlug = new Map()
  const order = []

  const ensureRegion = (regionSlug, node) => {
    if (!regionBySlug.has(regionSlug)) {
      const entry = node
        ? {
            id: node.id,
            heading: node.heading,
            path: node.path,
            postcodes: node.postcodes,
            locations: node.locations || [],
            children: []
          }
        : {
            id: `region-${regionSlug}`,
            heading: slugToHeading(regionSlug),
            path: `/${SHOWROOM_BASE}/${regionSlug}/`,
            postcodes: undefined,
            locations: [],
            children: []
          }
      regionBySlug.set(regionSlug, entry)
      order.push(regionSlug)
    } else if (node) {
      const existing = regionBySlug.get(regionSlug)
      regionBySlug.set(regionSlug, {
        ...existing,
        id: node.id,
        heading: node.heading,
        path: node.path,
        postcodes: node.postcodes ?? existing.postcodes,
        locations:
          node.locations && node.locations.length
            ? node.locations
            : existing.locations
      })
    }
  }

  for (const node of showroomNodes) {
    const parsed = parseShowroomListPath(node.path)
    if (!parsed) continue

    if (parsed.type === "region") {
      ensureRegion(parsed.regionSlug, node)
    } else {
      ensureRegion(parsed.regionSlug, null)
      regionBySlug.get(parsed.regionSlug).children.push(node)
    }
  }

  return order.map(slug => regionBySlug.get(slug))
}

const LocationMapWithFinder = ({ showVirtualConsultation }) => {
  const {
    auShowroomList,
    nzShowroomList,
    auLocations,
    nzLocations
  } = useStaticQuery(
    graphql`
      query locationMapWithFilter {
        auShowroomList: allDatoCmsShowroomList(
          sort: { fields: position }
          locale: "en-AU"
          filter: { country: { eq: "AU" }, locales: { eq: "en-AU" } }
        ) {
          nodes {
            id
            heading
            path
            postcodes
            locations {
              id
              shortTitle
              title
              businessName
              path
            }
          }
        }

        nzShowroomList: allDatoCmsShowroomList(
          sort: { fields: position }
          locale: "en-NZ"
          filter: { country: { eq: "NZ" }, locales: { eq: "en-NZ" } }
        ) {
          nodes {
            id
            heading
            path
            postcodes
            locations {
              id
              shortTitle
              title
              businessName
              path
            }
          }
        }

        nzLocations: allDatoCmsLocation(
          locale: "en-NZ"
          filter: { country: { eq: "NZ" }, locales: { eq: "en-NZ" } }
        ) {
          nodes {
            id
            areasServed
            path
            businessName
            vertical
            horizontal
            hidePin
            isJacuzzi: jacuzziExclusiveShowroom
          }
        }

        auLocations: allDatoCmsLocation(
          locale: "en-AU"
          filter: { country: { eq: "AU" }, locales: { eq: "en-AU" } }
        ) {
          nodes {
            id
            areasServed
            path
            businessName
            vertical
            horizontal
            hidePin
            isJacuzzi: jacuzziExclusiveShowroom
          }
        }
      }
    `
  )

  const siteLanguage = process.env.GATSBY_LANGUAGE

  const showroomNodes =
    siteLanguage === "en-AU" ? auShowroomList.nodes : nzShowroomList.nodes

  const locationNodes =
    siteLanguage === "en-AU" ? auLocations.nodes : nzLocations.nodes

  const groupedLocationLists = buildGroupedLocationLists(showroomNodes)
  const useGroupedSidebar = groupedLocationLists.length > 0

  const removeStates = new Set([
    "Western Australia",
    "ACT",
    "South Australia",
    "New South Wales",
    "Northern Territory",
    "Queensland",
    "Tasmania"
  ])

  const filteredShowrooms = showroomNodes.filter(
    obj => !removeStates.has(obj.heading)
  )

  return (
    <div className={style[`location-map-with-finder`]}>
      <Finder
        groupedLocationLists={useGroupedSidebar ? groupedLocationLists : null}
        locationLists={useGroupedSidebar ? null : filteredShowrooms}
        allLocations={showroomNodes}
        locations={locationNodes}
        showVirtualConsultation={showVirtualConsultation}
      >
        <LocationMap map={siteLanguage} locations={locationNodes} />
      </Finder>
    </div>
  )
}

export default LocationMapWithFinder
